import React, { Suspense, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Loader2, Clock } from 'lucide-react';
import Sidebar from '@/components/dashboard/Sidebar';
import RouteErrorBoundary from '@/components/app/RouteErrorBoundary';
import { CopilotDrawer } from '@/components/copilot/CopilotDrawer';
import { ContentModalsProvider } from '@/components/automation/content/ContentModalsProvider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PageHeaderProvider } from '@/contexts/PageHeaderContext';
import DynamicHeader from '@/components/dashboard/DynamicHeader';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectAllTeams } from '@/store/slices/teams/selectors';
import { getUserId } from '@/utils/helpers/helpers';
import { fetchTeams } from '@/store/slices/teams/teamsSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { maybeFetchWorkspaceSettings } from '@/store/slices/workspaceSettings/workspaceSettingsSlice';

const LoadingSpinner = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

/**
 * Main dashboard layout that includes the sidebar and main content area
 * Wraps content in route-level error boundary and suspense fallback
 */
const DashboardLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const { settings } = useSelector((state: RootState) => state.workspaceSettings);
  const teams = useAppSelector(selectAllTeams);
  const currentUserId = getUserId();
  const teamMappedByIdsWithUserIds = teams.map((team) => ({ id: team.id, userIds: team.teamMembers.map((member) => member.id) }));
  const userTeamId = teamMappedByIdsWithUserIds.find((team) => team.userIds.includes(currentUserId))?.id;
  const doesUserHaveAccessToCopilot = settings?.copilotsEnabled && !settings?.excludedTeamsFromCopilot?.includes(userTeamId) && userTeamId;

  useEffect(() => {
    dispatch(fetchTeams() as any);
    dispatch(maybeFetchWorkspaceSettings() as any);
  }, [dispatch]);

  return (
    <PageHeaderProvider>
      <div className="h-screen flex overflow-hidden bg-background">
        <Sidebar />
        <main className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
          <DynamicHeader />
          <ScrollArea className="flex-1 min-h-0 overflow-hidden">
            <RouteErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <div className="flex-1 min-h-0 overflow-hidden">
                  <Outlet />
                </div>
              </Suspense>
            </RouteErrorBoundary>
          </ScrollArea>
        </main>

        {/* Add CopilotDrawer here so it's available throughout the app */}
        {
          doesUserHaveAccessToCopilot && (
            <CopilotDrawer />
          )
        }

        {/* Add ContentModalsProvider here so all content modals are available throughout the app */}
        <ContentModalsProvider />
      </div>
    </PageHeaderProvider>
  );
};

export default DashboardLayout;
